/**
 * THE fold. One precedence order, one aggregation rule, one authority floor.
 *
 * Spec §5.8 — "No interface may implement its own truth logic." Before this
 * module the system answered "what is this work's verdict?" with FIVE
 * independent implementations that disagreed on real inputs:
 *
 *   protocol/verdict.ts      aggregateDeterminations       {PASS,NOT_RUN} -> NOT_RUN
 *   protocol/completion.ts   worstVerificationStatus       {PASS,NOT_RUN} -> NOT_RUN
 *   domain/state-machines.ts aggregateDeterminationStatus  {PASS,NOT_RUN} -> UNKNOWN
 *   domain/state.ts          aggregateVerificationStatus   {PASS,NOT_RUN} -> UNKNOWN
 *   policy-studio.ts         evaluatePolicyAgainstCase     {PASS,NOT_RUN} -> UNKNOWN
 *
 * and on whether an all-PASS run with no `deterministic`/`human` evaluator
 * behind it may be called PASS (only `domain/state.ts` refused).
 *
 * ── Which law won, and why it is the strict one ──
 *
 * The unified law is the POINTWISE MAXIMUM of the five, never a merge or an
 * average. On every input it is at least as severe as the most severe tier,
 * because a unification that is more permissive than the strictest current
 * tier is a regression wearing a refactor's clothes. Concretely it takes:
 *
 *   - the NOT_RUN -> UNKNOWN promotion from the domain tiers. An aggregate of
 *     {PASS, NOT_RUN} is not "not run" — part of it ran and passed, so NOT_RUN
 *     is a claim about the whole that a member contradicts. UNKNOWN is the
 *     honest reading: coverage is partial, so "every obligation holds" is
 *     neither established nor refuted. §5.4 forbids insufficient evidence
 *     becoming success; both candidates cleared that, but only UNKNOWN also
 *     avoids asserting something false about the members that DID run. By the
 *     precedence order below — which both protocol-tier copies already
 *     declared — UNKNOWN outranks NOT_RUN, so this is a strengthening.
 *
 *   - the PASS authority floor from `domain/state.ts`, which the protocol tier
 *     was MISSING. An all-PASS set carried entirely by `semantic` or
 *     `imported` evaluators does not become PASS. Adopting the floor here
 *     applies a refusal the protocol tier never made; dropping it would have
 *     been the worst available outcome of this change.
 *
 * ── The missing vocabulary this made visible, and then closed ──
 *
 * Under one law a NON-EMPTY aggregate never returned NOT_RUN: any member with
 * no finding promoted to UNKNOWN. That was not information this module
 * destroyed — it was the four-value vocabulary being too small to say
 * "pending" separately from "could not tell" (§5.4 wants both; `NOT_RUN` means
 * "we did not look", not "too early"). Localizing the gap in ONE function
 * instead of five is what made both halves closable as row insertions, which
 * is what MISMATCH (2026-09-12) and then PENDING (below) turned out to be.
 * `_everyDeclaredStatusIsRanked` turns "added the enum value but not the rank"
 * into a COMPILE error rather than a silent default.
 *
 * ── MISMATCH: the half of that gap this change closes (2026-09-12) ──
 *
 * The system could say "not PASS". It could not say "the evidence shows this
 * was done wrong". Both facts landed on FAIL, so an oracle that REFUSED TO
 * CONFIRM and an oracle that CONTRADICTED THE CLAIM were indistinguishable in
 * the verdict (`engine.ts` did `oracleRefusal ? "FAIL" : status`, and its own
 * comment conceded the collapse). §5.4 requires verified / mismatched /
 * pending / unknown to be distinguishable.
 *
 * `MISMATCH` is the ACCUSATION; `FAIL` keeps being the REFUSAL. That direction
 * is the one that preserves stored receipts: MISMATCH is a strict REFINEMENT
 * of FAIL, so every receipt already signed FAIL still means exactly what it
 * meant — conclusive, not PASS — merely less specific than a verdict minted
 * today can be. Nothing re-reads an old FAIL as an accusation, which is the
 * failure mode the opposite direction would have had.
 *
 * ── Why MISMATCH ranks ABOVE FAIL ──
 *
 * Take one obligation CONTRADICTED and another merely UNCONFIRMED. The
 * aggregate is one sentence about the whole, and both candidates are true:
 *
 *   "at least one obligation is affirmatively violated"  (MISMATCH) — names a
 *      DEFECT the submitter must fix.
 *   "at least one obligation was not confirmed"          (FAIL) — names a GAP
 *      the verifier could close by running the oracle, and buries the
 *      violation underneath it.
 *
 * A verification product that hides a demonstrated violation behind a coverage
 * hole has picked the less informative of two true statements, so MISMATCH is
 * the most severe rank. It changes no existing verdict: no member can hold
 * MISMATCH unless an oracle resolved and disagreed, and FAIL > UNKNOWN >
 * NOT_RUN > PASS is untouched.
 *
 * What is NOT claimed: MISMATCH is `conclusive: true, admitsPass: false`,
 * identical to FAIL on both predicates. It is not a new KIND of verdict, it is
 * a more precise name for one end of an existing one, and every refusal FAIL
 * used to make it makes too.
 *
 * ── PENDING: the other half, closed (2026-09-12) ──
 *
 * The system could say "we did not look" (`NOT_RUN`) and "we looked and could
 * not tell" (`UNKNOWN`). It could not say "TOO EARLY TO TELL, ASK AGAIN BY
 * <deadline>". Those are not shades of one fact, because their CONSEQUENCES
 * differ and the consequence is the whole point of a verdict:
 *
 *   UNKNOWN  escalates to a HUMAN, who is asked to fix an instrument.
 *   PENDING  re-queues a JOB, and nobody is paged at all.
 *
 * Routing "not yet" into UNKNOWN pages a person for a race that would have
 * settled itself; routing it into NOT_RUN claims nothing was looked at when
 * something was. Spec §20's operation deadline had nothing to resolve INTO,
 * and nothing swept a non-conclusive verdict into a definite one — which is
 * why receipts could sit non-conclusive forever with no next step named.
 *
 * ── Why PENDING is the LEAST severe non-conclusive status ──
 *
 * Ranked immediately above PASS and BELOW NOT_RUN, so the order is
 * MISMATCH > FAIL > UNKNOWN > NOT_RUN > PENDING > PASS.
 *
 * The argument is MISMATCH's, run in the opposite direction. An aggregate is
 * one sentence about the whole, so where two true sentences are available the
 * law takes the one a reader can act on NOW. PENDING is the only status whose
 * instruction is "come back later", and coming back later is only the right
 * instruction when there is nothing to do YET — that is, when every other
 * member is PASS. Against any other member, deferring the whole aggregate
 * behind the one member that is merely early SUPPRESSES information that
 * already exists:
 *
 *   {MISMATCH, PENDING} -> MISMATCH.  A demonstrated violation is not "wait
 *      and see". This is the same refusal that put MISMATCH above FAIL.
 *   {FAIL, PENDING}     -> FAIL.      A refusal to certify is answerable now;
 *      a timer must not hold it.
 *   {UNKNOWN, PENDING}  -> UNKNOWN.   The load-bearing case. An instrument gap
 *      is not fixed by waiting, so reporting PENDING would tell the reader to
 *      come back later about something that will look identical when they do,
 *      and would suppress the escalation indefinitely. Hiding a coverage hole
 *      behind a timer is the same defect as hiding a violation behind a
 *      coverage hole.
 *   {NOT_RUN, PENDING}  -> UNKNOWN.   Same reason, via NOT_RUN's existing
 *      promotion: "part of this was never looked at" is a fact that holds
 *      whatever the clock does.
 *   {PASS, PENDING}     -> PENDING.   The only shape that defers. Everything
 *      determined has passed and the only thing missing is time.
 *
 * So a single PENDING member NEVER changes an aggregate that contains any
 * other non-PASS member — which is also why this row insertion moves no
 * existing verdict. Ranking PENDING any higher would have made it a MASK.
 *
 * It changes no stored receipt for the stronger reason too: no determination
 * can hold PENDING unless an adapter declared a `pending` finding WITH a
 * deadline (`observation-adapter.ts`), a shape that did not exist until now.
 * Measured 2026-09-12 against the production store: 81 signed receipts, PASS
 * 78 / FAIL 2 / NOT_RUN 1, and not one of them can be re-read as PENDING
 * because PENDING is not a value any of them contains.
 *
 * WHAT PENDING IS NOT: it is not `conclusive`, so it can never carry an
 * aggregate to PASS, and it is not a status that stands alone — see
 * `resolvePendingVerdict` at the bottom of this file. A PENDING with no
 * deadline does not exist: the determination schema refuses `pending: true`
 * without `{ pendingUntil, pendingResolvesTo }` and refuses the reverse. What
 * a passed deadline MEANS to a reader is `resolvePendingVerdict`'s answer, and
 * every reader now asks for it at READ time through `resolveStoredVerdict` —
 * see that function for where the deadline lives and why nothing writes back.
 */
import { hasReachedDeadline } from "./instant.js";
/**
 * The single precedence order, most severe first, carrying the predicates the
 * fold needs as DATA rather than as branches. A new status is a new row.
 *
 *   conclusive       the status is a finding. `false` means the evaluator
 *                    reached no finding (it did not run, it could not tell, or
 *                    it is not yet due), which makes the whole aggregate
 *                    non-conclusive. MISMATCH and FAIL are both findings; they
 *                    differ in WHICH finding, not in whether one was reached.
 *   admitsPass       the aggregate may be reported as this status when every
 *                    member holds it. True for PASS alone.
 *   nonConclusiveAs  what an aggregate reports when THIS row is its worst
 *                    member and the member reached no finding. Non-null on
 *                    exactly the non-conclusive rows, and read by the fold, so
 *                    the NOT_RUN -> UNKNOWN promotion below is a table entry a
 *                    reader can see rather than a branch buried in a function.
 *                    `null` on a conclusive row, where a non-conclusive
 *                    aggregate is not a reachable question.
 */
export const VERIFICATION_STATUS_PRECEDENCE = [
    { status: "MISMATCH", conclusive: true, admitsPass: false, nonConclusiveAs: null },
    { status: "FAIL", conclusive: true, admitsPass: false, nonConclusiveAs: null },
    { status: "UNKNOWN", conclusive: false, admitsPass: false, nonConclusiveAs: "UNKNOWN" },
    // The promotion the domain tiers won, now stated as data: an aggregate of
    // {PASS, NOT_RUN} is not "not run", it is "partially covered".
    { status: "NOT_RUN", conclusive: false, admitsPass: false, nonConclusiveAs: "UNKNOWN" },
    // PENDING reports ITSELF rather than promoting, which is the entire point of
    // adding it: "ask again by a deadline" is a different instruction from
    // "escalate to a human", and promoting it to UNKNOWN would erase the one
    // consequence that distinguishes them.
    { status: "PENDING", conclusive: false, admitsPass: false, nonConclusiveAs: "PENDING" },
    { status: "PASS", conclusive: true, admitsPass: true, nonConclusiveAs: null },
];
/**
 * The status a non-conclusive aggregate reports when its worst member does not
 * name one — and the status an all-PASS set falls to when the authority floor
 * refuses it, which is the only remaining branch that needs a constant.
 */
export const NON_CONCLUSIVE_AGGREGATE = "UNKNOWN";
/** The status an aggregate over nothing at all reports. */
export const EMPTY_AGGREGATE = "NOT_RUN";
/**
 * The evaluator authorities that can carry an all-PASS set to PASS. An
 * authority outside this list — `semantic`, `imported`, or any value a future
 * schema adds — does NOT satisfy the floor. The check is a membership test
 * rather than a `!== "semantic"` exclusion so that it fails CLOSED: a new
 * authority is untrusted until it is listed here deliberately.
 */
export const PASS_AUTHORITY_FLOOR = ["deterministic", "human"];
/**
 * The authority a call site declares when it is folding statuses that carry no
 * evaluator authority at all — already-aggregated window verdicts, or a pure
 * status projection. The floor cannot be evaluated over such input, so it is
 * not applied. This is the ONE value that waives it: every other string,
 * recognized or not, must appear in `PASS_AUTHORITY_FLOOR` to admit PASS.
 *
 * It must be written explicitly at the call site — there is no default — and
 * mixing it with attributed members is refused below, because a caller that
 * had authority for some members and dropped it for others is not folding an
 * unattributed set, it is discarding evidence.
 */
export const UNATTRIBUTED_AUTHORITY = "unattributed";
/**
 * The authority of a COVERAGE HOLE — a required obligation that produced no
 * determination at all. Distinct from `UNATTRIBUTED_AUTHORITY`: that one says
 * "this call site does not model authority", while this one says "there is no
 * evaluator behind this member". It must never waive the floor and must never
 * help satisfy it, which is exactly what any non-floor, non-waiver string does.
 */
export const NO_AUTHORITY = "none";
const _everyDeclaredStatusIsRanked = true;
void _everyDeclaredStatusIsRanked;
const _everyRankNamesADeclaredStatus = true;
void _everyRankNamesADeclaredStatus;
/**
 * Fold a set of determinations to one verdict.
 *
 * Callers own COVERAGE — deciding which determinations count, and synthesizing
 * a member for a required obligation that produced none. They do not own
 * TRUTH: once the set is chosen, this function alone decides what it means.
 *
 * @throws if attributed and unattributed members are mixed.
 */
export function foldVerificationStatus(members) {
    if (members.length === 0)
        return EMPTY_AGGREGATE;
    const worst = VERIFICATION_STATUS_PRECEDENCE.find((rank) => members.some((member) => member.status === rank.status));
    // Unreachable while the totality assertions above hold: every member status
    // is ranked, so a non-empty set always matches a rank. Refuse rather than
    // invent a verdict if that ever stops being true.
    if (!worst) {
        throw new Error(`foldVerificationStatus: no ranked status matched [${members.map((m) => m.status).join(", ")}]`);
    }
    // A member with no finding makes the aggregate non-conclusive, whatever the
    // other members said. WHICH non-conclusive status is reported is the row's
    // own `nonConclusiveAs`: UNKNOWN for NOT_RUN (the promotion) and for UNKNOWN
    // itself, PENDING for PENDING.
    if (!worst.conclusive) {
        // Both fields are consulted, and their correlation is ENFORCED rather than
        // assumed: a row declaring itself non-conclusive with no aggregate target
        // would otherwise fold to `undefined` and be signed into a receipt.
        //
        // The widening annotation is deliberate. Read straight off the `as const`
        // table, TypeScript proves this branch unreachable and narrows `worst` to
        // `never` — which is the correlation holding at COMPILE time, and is worth
        // having. But a compile-time proof about this source file says nothing
        // about the built dist a mutant or a hand-edit produces, so the runtime
        // refusal is kept alive by widening first. It refuses rather than
        // inventing a verdict, the same posture as the unranked-status throw.
        const nonConclusiveAs = worst.nonConclusiveAs;
        if (nonConclusiveAs === null) {
            throw new Error(`foldVerificationStatus: rank ${worst.status} is non-conclusive but names no aggregate status`);
        }
        return nonConclusiveAs;
    }
    // A conclusive status that is not PASS is the verdict. MISMATCH (an
    // affirmative violation) outranks FAIL (a refusal to confirm), so a
    // demonstrated violation is never buried under a coverage hole.
    if (!worst.admitsPass)
        return worst.status;
    // Every member is PASS. The authority floor decides whether that may be said.
    return satisfiesPassAuthorityFloor(members) ? worst.status : NON_CONCLUSIVE_AGGREGATE;
}
function satisfiesPassAuthorityFloor(members) {
    const attributed = members.filter((member) => member.authority !== UNATTRIBUTED_AUTHORITY);
    if (attributed.length === 0)
        return true; // floor not evaluable over unattributed input
    if (attributed.length !== members.length) {
        throw new Error("foldVerificationStatus: members mix attributed and unattributed authority; " +
            "a set that carries authority for some members and not others is discarding evidence");
    }
    return attributed.some((member) => PASS_AUTHORITY_FLOOR.includes(member.authority));
}
/**
 * The fold over statuses that carry no evaluator authority. Equivalent to
 * `foldVerificationStatus` with every member `UNATTRIBUTED_AUTHORITY` — kept
 * as a named entry point so the waiver is visible at every site that takes it,
 * and greppable across the tree.
 */
export function foldVerificationStatuses(statuses) {
    return foldVerificationStatus(statuses.map((status) => ({ status, authority: UNATTRIBUTED_AUTHORITY })));
}
/**
 * Exhaustiveness helper for call sites that must switch on a status. Reaching
 * it is a compile error the moment a new status is declared, so a `PENDING`
 * receipt cannot fall through a `default` and be rendered as something else.
 */
export function assertExhaustiveStatus(value) {
    throw new Error(`unhandled verification status: ${String(value)}`);
}
/**
 * The resolutions, as data, for call sites that must validate one off the wire
 * (a zod enum, a stored column) rather than off the type. Kept in precedence
 * order so a reader sees immediately that every one of them is at least as
 * severe as PENDING.
 */
export const PENDING_RESOLUTIONS = [
    "MISMATCH",
    "FAIL",
    "UNKNOWN",
    "NOT_RUN",
];
/**
 * The resolution a caller that declares no other one gets.
 *
 * UNKNOWN, deliberately — "no finding, escalate". A window that closed without
 * an answer is a gap in the verifying side's coverage, and the honest report of
 * a gap is UNKNOWN. It is NOT FAIL (that would name the submitter answerable
 * for a deadline the verifier failed to keep) and NOT MISMATCH (that would
 * mint an accusation out of an absence — the exact direction
 * `oracle-resolution.ts` refuses). A caller who KNOWS the window closing is
 * itself the finding — CRM1's case 3, where a required follow-up that never
 * appeared before the deadline genuinely is an omission — says so by declaring
 * MISMATCH explicitly. Never inferred.
 */
export const DEFAULT_PENDING_RESOLUTION = "UNKNOWN";
/**
 * Resolve a stored verdict AT READ TIME.
 *
 * READ TIME IS THE ONLY HOST. THERE IS NO SWEEP, and this comment used to say
 * there was one: a `pending-sweep.ts` that enqueued a job at `expiresAt`, "re-
 * verified, and wrote the settled verdict back". It was deleted 2026-09-12
 * because it could not do that, and because nothing ever called it — neither
 * the enqueue nor the drain had a single caller outside its own test.
 *
 * It could not settle storage for two structural reasons, either one
 * sufficient. First, the durable runner DISCARDS an effect payload: the
 * settled `{ receiptId, status }` reached `completeWithEffect`, which keeps
 * only `payloadDigest: sha256(payload)` on the effect record and stores no
 * payload column at all, so the answer was hashed and thrown away and no
 * reader of the job store could recover it (§5 of
 * `test-verification-pending-verdict.mjs` drives this over the real store).
 * Second, a verdict receipt is SIGNED and append-only — `aggregateStatus` sits
 * inside the payload bound by `payloadDigest` and the signature, and
 * `verification_protocol_objects` is written INSERT-OR-IGNORE with no UPDATE
 * anywhere — so settling one in place is not merely unimplemented, it is
 * barred. The lawful way to change a verdict is a NEW receipt carrying
 * `supersedesReceiptId`, which needs an evidence binding, a compilation
 * digest, a determination set and a signer; a sweep holding a bare status
 * string could never mint one.
 *
 * That is also why a job was never the right host for the LAW: a brake hosted
 * inside the thing it brakes never engages. If the only thing that can end a
 * PENDING is a job, then a queue that never drains, a lease that never
 * releases, or a runner nobody started leaves the receipt pending forever, and
 * the defect is reintroduced by the mechanism meant to close it. So the
 * deadline is enforced HERE, by a pure function that is total and fires on
 * whatever path calls it.
 *
 * THE READ PATHS ARE WIRED (2026-09-12). This comment used to warn that they
 * were not — that `resolvePendingVerdict` had no product caller at all, so a
 * stored PENDING read PENDING past its deadline on every consumer. They now go
 * through {@link resolveStoredVerdict} at the bottom of this file, which is the
 * one place that knows where a stored verdict keeps its deadline: the
 * enforcement disposition (`protocol/enforcement.ts`), the talkback next-action
 * (`protocol/talkback.ts`), the canonical verdict handed to a rail
 * (`surfaces/adapters.ts`), the work-chain route
 * (`excellent/server/routes/verification-work-chain.ts`) and the three client
 * readers (`d1-loop-status.tsx`, `verifier-home.tsx`, `home-page.tsx`). None of
 * them does its own deadline arithmetic — that would be five divergent folds
 * again, which is the defect the top of this file exists to have ended.
 *
 * Still NOT resolved through this law, and named rather than implied: the
 * `RECEIPT_ISSUED` event body in `protocol/service.ts` and the gate-record
 * snapshot in `excellent/server/routes/tail.ts` both read a receipt the line
 * above them JUST MINTED. Those are records of what was issued, at the instant
 * it was issued; they are not reads of a stored verdict, and resolving them
 * would be a provable no-op.
 *
 * TOTAL, AND IT NEVER RETURNS PENDING FOR AN UNREADABLE DEADLINE.
 * `hasReachedDeadline` is three-valued on purpose: `null` means "I cannot tell
 * whether the deadline has passed", and a caller that treats that as "not yet"
 * has written the original bug. An unreadable or absent `expiresAt` therefore
 * resolves IMMEDIATELY to `expiresTo`, because the only alternative is a
 * pending state with no way out.
 *
 * A verdict that is not PENDING is returned UNCHANGED, which is what keeps
 * every stored receipt meaning what it meant: PASS, FAIL, UNKNOWN, NOT_RUN and
 * MISMATCH do not have deadlines and this function does not give them one.
 */
export function resolvePendingVerdict(input) {
    if (input.status !== "PENDING") {
        return { status: input.status, resolved: false, reason: "not-pending" };
    }
    // A PENDING with no pending record at all cannot be re-read as PENDING: there
    // is nothing to resolve it, which is exactly the state that must not exist.
    if (!input.pending) {
        return { status: DEFAULT_PENDING_RESOLUTION, resolved: true, reason: "deadline-missing" };
    }
    const reached = hasReachedDeadline(input.now, input.pending.expiresAt);
    if (reached === null) {
        return { status: input.pending.expiresTo, resolved: true, reason: "deadline-unreadable" };
    }
    if (reached) {
        return { status: input.pending.expiresTo, resolved: true, reason: "deadline-passed" };
    }
    return { status: "PENDING", resolved: false, reason: "window-open" };
}
/**
 * The deadlines one PENDING determination is waiting on.
 *
 * A window with no `pendingResolvesTo` is NOT returned: it has nothing to
 * become, so it is not a way out, and the caller routes it to the law's
 * `pending: null` branch, which resolves to {@link DEFAULT_PENDING_RESOLUTION}
 * rather than inventing one. A window whose DEADLINE is missing or unparseable
 * keeps its declared resolution and is handed to the law as an unreadable
 * `expiresAt` — which resolves IMMEDIATELY, because `hasReachedDeadline` is
 * three-valued and "I cannot tell whether it expired" must never read as "not
 * yet". `oracleDeterminationSchema` refuses both halves of that at the parse
 * boundary; this handles them anyway, because the raw-record readers never
 * reach that boundary.
 */
export function pendingWindowsOf(determination) {
    const windows = [];
    for (const oracle of determination.oracleDeterminations ?? []) {
        if (!oracle || oracle.pending !== true)
            continue;
        const expiresTo = oracle.pendingResolvesTo;
        if (expiresTo === null || expiresTo === undefined)
            continue;
        windows.push({ expiresAt: oracle.pendingUntil ?? "", expiresTo });
    }
    return windows;
}
/**
 * READ a stored verdict. The status a consumer must render or act on.
 *
 * ── Why resolution happens HERE and not in storage ──
 *
 * A verdict receipt is SIGNED and append-only: `aggregateStatus` sits inside
 * the payload bound by `payloadDigest` and the signature, and
 * `verification_protocol_objects` is INSERT-OR-IGNORE with no UPDATE anywhere.
 * Settling one in place is barred, not merely unimplemented. So the stored
 * bytes keep saying PENDING forever and it is the READER's job to know what
 * that means NOW — which is also why `now` is a parameter. A resolver that read
 * the clock itself would be untestable, and every gate built on it flaky.
 *
 * ── Only a stored PENDING is touched ──
 *
 * A stored aggregate that is not PENDING is returned UNCHANGED, which is the
 * law's own rule one level up: PASS, FAIL, UNKNOWN, NOT_RUN and MISMATCH do not
 * have deadlines and this function does not give them one. That guard is also
 * what makes the re-fold below SOUND. A PENDING aggregate can only have been
 * produced by a set whose every counted member was PASS or PENDING (see
 * `VERIFICATION_STATUS_PRECEDENCE` — a single PENDING never outranks any other
 * non-PASS member), so under this guard there is no coverage hole to lose and
 * no counted member whose status could change the answer except the PENDING
 * ones. Both facts are false for a non-PENDING aggregate, which is exactly why
 * this does not re-fold one.
 *
 * ── What is folded ──
 *
 * Each PENDING member is resolved through {@link resolvePendingVerdict} — one
 * member per window it is waiting on, because a determination waiting on two
 * oracles leaves PENDING at the EARLIER deadline, not the later one — and the
 * resolutions are folded through {@link foldVerificationStatuses}. Two
 * consequences worth naming, because both are the existing law rather than a
 * new rule invented here:
 *
 *   - {FAIL, PENDING} -> FAIL. A window that closed on a refusal is answerable
 *     now; a second window still open must not hold it.
 *   - A window expiring to NOT_RUN reads as UNKNOWN, via the fold's NOT_RUN ->
 *     UNKNOWN promotion. "Part of this was never looked at" is not a statement
 *     the whole can make when another part passed.
 *
 * The PASS members are not folded in: they cannot change the answer (PASS is
 * the least severe rank, and the authority floor is only reachable when EVERY
 * member is PASS, which this branch excludes), and including them would drag in
 * optional members the original aggregation may deliberately have excluded via
 * `optionalFailuresAffectAggregate`.
 *
 * ── Expiry can never yield PASS ──
 *
 * By construction, twice over. Every folded member comes from a PENDING
 * resolution, whose type is `PendingResolution` — `Exclude<VerificationStatus,
 * "PASS" | "PENDING">` — or is PENDING itself, so no member is ever PASS and
 * the fold's all-PASS branch is unreachable. The runtime refusal below is kept
 * anyway, on the same reasoning the fold's own `nonConclusiveAs === null` throw
 * is kept: a compile-time proof about this source file says nothing about the
 * built dist a mutant or a hand-edit produces.
 */
export function resolveStoredVerdict(input) {
    const stored = input.verdict.aggregateStatus;
    if (stored !== "PENDING") {
        return { status: stored, resolved: false, reason: "not-pending" };
    }
    // Keyed on the member's own STATUS, never on "carries a pending window": a
    // determination an oracle REFUSED is FAIL even when a second, slower oracle
    // on the same obligation is still pending (`engine.ts` picks the dissent by
    // severity and keeps every oracle record). Reading that member as pending
    // would hide a refusal behind a timer.
    const pendingMembers = (input.verdict.determinations ?? []).filter((determination) => determination?.status === "PENDING");
    if (pendingMembers.length === 0) {
        // The aggregate says PENDING and no member does, so nothing in this receipt
        // can ever end the wait. The law's own answer for a PENDING with no pending
        // record: resolve now, to the default, rather than wait forever.
        return resolvePendingVerdict({ status: stored, pending: null, now: input.now });
    }
    const resolutions = pendingMembers.flatMap((determination) => {
        const windows = pendingWindowsOf(determination);
        if (windows.length === 0) {
            return [resolvePendingVerdict({ status: "PENDING", pending: null, now: input.now })];
        }
        return windows.map((pending) => resolvePendingVerdict({ status: "PENDING", pending, now: input.now }));
    });
    const status = foldVerificationStatuses(resolutions.map((resolution) => resolution.status));
    if (status === "PASS") {
        throw new Error("resolveStoredVerdict: a pending verdict resolved to PASS, which PendingResolution forbids");
    }
    // WHICH window answered, for disclosure only — the status above is the
    // verdict. Chosen by severity off the one precedence table, never by array
    // position, for the same reason the fold is: with one window closed on a
    // refusal and another still open, the refusal is what happened.
    const governing = VERIFICATION_STATUS_PRECEDENCE.flatMap((rank) => resolutions.filter((resolution) => resolution.status === rank.status))[0] ?? resolutions[0];
    return { status, resolved: status !== "PENDING", reason: governing.reason };
}
